<template>
  <div id="ParentProductOnlineStatus" class="parent-product-online-status-page purchase-mgmt-page">
    <div class="breadcrumb-box">
      <el-breadcrumb separator="/">
        <el-breadcrumb-item>产品管理</el-breadcrumb-item>
        <el-breadcrumb-item :to="{ path: '/client/parentProductMgmt' }">父产品管理</el-breadcrumb-item>
        <el-breadcrumb-item>上线情况</el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <div ref="header" class="page-header">
      <div class="filter-card header-box">
        <div class="filter-row">
          <div class="filter-item">
            <span class="filter-label">父产品</span>
            <span class="product-summary">{{ parentProductSummary }}</span>
          </div>
          <div class="filter-item">
            <span class="toolbar-tip">颜色 {{ colorColumns.length }} 种 · 机型 {{ modelRows.length }} 款</span>
          </div>
        </div>
        <div class="filter-toolbar">
          <div class="legend-box">
            <span class="legend-item"><i class="status-dot active"></i>已上线（Active）</span>
            <span class="legend-item"><i class="status-dot inactive"></i>非 Active</span>
            <span class="legend-item legend-empty">空白表示无对应子商品</span>
          </div>
        </div>
      </div>
    </div>

    <div class="table-panel" ref="tablePanel">
      <div class="table-container online-status-container" v-loading="loading" element-loading-text="加载中...">
        <div class="online-status-scroll">
          <table class="online-status-matrix">
            <thead>
              <tr>
                <th class="model-col sticky-col">机型</th>
                <th v-for="color in colorColumns" :key="color.key" class="color-col">
                  <el-tooltip :content="color.name" placement="top">
                    <span class="color-col-label">{{ color.name }}</span>
                  </el-tooltip>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="model in modelRows" :key="model.model">
                <td class="model-col sticky-col">
                  <el-tooltip :content="model.name" placement="right">
                    <span class="model-col-label">{{ model.name }}</span>
                  </el-tooltip>
                </td>
                <td v-for="color in colorColumns" :key="color.key + '-' + model.model" class="status-cell">
                  <el-tooltip v-if="getCellStatus(color.key, model)" :content="getCellTitle(color.key, model)" placement="top">
                    <span class="status-dot" :class="getCellStatus(color.key, model)"></span>
                  </el-tooltip>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { requestModules } from "@r";
import { colors, iPhoneModels } from "../../utils/constant.js";

const ONLINE_STATUS_MIN_YEAR = 2016;
const ONLINE_STATUS_MAX_YEAR = 2025;

export default {
  data() {
    return {
      loading: false,
      productType: this.$route.query.productType,
      parentProduct: null,
      products: [],
      statusMap: {},
      colorColumns: [],
      modelRows: [],
      modelAliasMap: {},
    };
  },
  computed: {
    parentProductSummary() {
      if (!this.parentProduct) {
        return this.productType ? `类型编码 ${this.productType}` : "-";
      }
      return `${this.parentProduct.productName || "-"}（${this.parentProduct.shopName || "-"}）`;
    },
  },
  mounted() {
    this.initPage();
  },
  methods: {
    initPage() {
      if (!this.productType) {
        this.$message.error("缺少父产品类型参数");
        return;
      }
      this.ensureIPhoneModels().then(() => {
        this.modelRows = this.buildModelRows();
        this.buildModelAliasMap();
        this.loadData();
      });
    },
    ensureIPhoneModels() {
      if (iPhoneModels.length) {
        return Promise.resolve();
      }
      return requestModules.fetchPhoneCaseContantInfo({}).then(res => {
        iPhoneModels.length = 0;
        (res.mdata.iPhoneModels || []).forEach(model => iPhoneModels.push(model));
      });
    },
    buildModelRows() {
      const indexMap = new Map(iPhoneModels.map((item, index) => [item.model, index]));
      return iPhoneModels
        .filter(item => item.year >= ONLINE_STATUS_MIN_YEAR && item.year <= ONLINE_STATUS_MAX_YEAR)
        .sort((a, b) => {
          if (b.year !== a.year) {
            return b.year - a.year;
          }
          return (indexMap.get(a.model) || 0) - (indexMap.get(b.model) || 0);
        });
    },
    buildModelAliasMap() {
      const map = {};
      this.modelRows.forEach(item => {
        map[this.normalizeModelKey(item.model)] = item.model;
      });
      this.modelAliasMap = map;
    },
    normalizeModelKey(model) {
      return String(model || "")
        .trim()
        .replace(/\s+/g, "")
        .toLowerCase();
    },
    resolveCanonicalModel(productModel) {
      const normalized = this.normalizeModelKey(productModel);
      if (this.modelAliasMap[normalized]) {
        return this.modelAliasMap[normalized];
      }
      return String(productModel || "").trim();
    },
    loadData() {
      this.loading = true;
      Promise.all([requestModules.fetchParentProducts({ pageSize: 10000 }), requestModules.fetchProducts({ productType: this.productType, beginIndex: 0, pageSize: 10000 })])
        .then(([parentRes, productRes]) => {
          const parentList = parentRes.ldata || [];
          this.parentProduct = parentList.find(item => String(item.productType) === String(this.productType)) || null;
          this.products = productRes.ldata || [];
          this.colorColumns = this.buildColorColumns(this.parentProduct);
          this.buildStatusMap();
        })
        .catch(() => {
          this.$message.error("加载上线情况失败");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    buildColorColumns(parentProduct) {
      if (!parentProduct) {
        const colorKeys = [...new Set(this.products.map(item => item.productColor).filter(Boolean))];
        return colorKeys.map(key => ({
          key,
          name: this.getColorName(key),
        }));
      }
      let checkColors = parentProduct.checkColors;
      if (!Array.isArray(checkColors)) {
        try {
          checkColors = JSON.parse(checkColors || "[]");
        } catch (error) {
          checkColors = [];
        }
      }
      if (!checkColors.length) {
        const colorKeys = [...new Set(this.products.map(item => item.productColor).filter(Boolean))];
        return colorKeys.map(key => ({
          key,
          name: this.getColorName(key),
        }));
      }
      return checkColors.map(key => ({
        key,
        name: this.getColorName(key),
      }));
    },
    getColorName(colorKey) {
      let color = colors.find(item => item.key == colorKey);
      return color ? color.name : colorKey;
    },
    buildStatusMap() {
      const map = {};
      this.products.forEach(product => {
        const canonicalModel = this.resolveCanonicalModel(product.model);
        const key = this.buildCellKey(product.productColor, canonicalModel);
        if (!map[key]) {
          map[key] = [];
        }
        map[key].push(product);
      });
      this.statusMap = map;
    },
    buildCellKey(colorKey, model) {
      return `${colorKey || ""}__${String(model || "").trim()}`;
    },
    getCellProducts(colorKey, modelItem) {
      return this.statusMap[this.buildCellKey(colorKey, modelItem.model)] || [];
    },
    getCellStatus(colorKey, modelItem) {
      const list = this.getCellProducts(colorKey, modelItem);
      if (!list.length) {
        return "";
      }
      if (list.some(item => this.isActiveStatus(item.status))) {
        return "active";
      }
      return "inactive";
    },
    getCellTitle(colorKey, modelItem) {
      const list = this.getCellProducts(colorKey, modelItem);
      if (!list.length) {
        return "";
      }
      return list
        .map(item => {
          const sku = item["seller-sku"] || item.sku || "-";
          return `${sku} · ${this.normalizeStatusLabel(item.status)}`;
        })
        .join("；");
    },
    isActiveStatus(status) {
      return (
        String(status || "")
          .trim()
          .replace(/\r/g, "") === "Active"
      );
    },
    normalizeStatusLabel(status) {
      const label = String(status || "")
        .trim()
        .replace(/\r/g, "");
      return label || "未知状态";
    },
  },
};
</script>

<style lang="scss">
@import "../../assets/styles/purchaseMgmtPage.scss";

.parent-product-online-status-page {
  .breadcrumb-box {
    flex-shrink: 0;
    margin-bottom: 12px;
    padding: 12px 16px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    border: 1px solid #ebeef5;

    .el-breadcrumb {
      line-height: 1;
      font-size: 13px;
    }
  }

  .header-box {
    .product-summary {
      color: #303133;
      font-size: 13px;
      font-weight: 500;
    }

    .filter-toolbar {
      margin-top: 4px;
      padding-top: 10px;
      border-top: 1px dashed #ebeef5;
    }
  }

  .legend-box {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 16px;
    font-size: 12px;
    color: #606266;
  }

  .legend-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .legend-empty {
    color: #909399;
  }

  .online-status-container {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .online-status-scroll {
    flex: 1;
    min-height: 0;
    width: 100%;
    overflow: auto;
  }

  .online-status-matrix {
    width: max-content;
    min-width: 100%;
    border-collapse: collapse;
    table-layout: fixed;

    th,
    td {
      border: 1px solid #ebeef5;
      text-align: center;
      vertical-align: middle;
      background: #fff;
    }

    thead th {
      position: sticky;
      top: 0;
      z-index: 2;
      background: #f5f7fa;
      color: #303133;
      font-size: 12px;
      font-weight: 600;
      padding: 10px 8px;
      white-space: nowrap;

      &.color-col {
        padding: 8px 4px;
        white-space: normal;
      }
    }

    .model-col {
      width: 100px;
      max-width: 100px;
      text-align: left;
      padding: 8px;
      font-size: 12px;
      color: #303133;
    }

    .model-col-label {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      cursor: default;
    }

    .sticky-col {
      position: sticky;
      left: 0;
      z-index: 1;
      background: #fff;
      box-shadow: 2px 0 4px rgba(0, 0, 0, 0.04);
    }

    thead .sticky-col {
      z-index: 3;
      background: #f5f7fa;
    }

    .color-col {
      width: 60px;
      max-width: 60px;
    }

    .color-col-label {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 11px;
      line-height: 1.2;
      cursor: default;
    }

    .status-cell {
      width: 60px;
      max-width: 60px;
      height: 32px;
      padding: 0;
    }
  }

  .status-dot {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    vertical-align: middle;

    &.active {
      background: #67c23a;
    }

    &.inactive {
      background: transparent;
      border: 2px solid #909399;
      box-sizing: border-box;
    }
  }
}
</style>
